// @flow

import {
  breakpointsPx,
} from '../../../helpers';
import type { Dimensions } from '../../../types';

/**
 * In this function, we transform the dimensions to a set of media queries. These queries
 * make sure the right viewport width is given at the right breakpoint.
 *
 * An example of data that could come in:
 * breakpoints: { sm: 567, md: 768, lg: 992, xl: 1200 }
 * dimensions: { default: 1, sm: 0.5, md: 0.4, lg: 0.3, xl: 0.2 }
 * maxWidth: 900
 *
 * The output would be:
 * `
 *    (max-width: 566px) 100vw,
 *    (max-width: 767px) 50vw,
 *    (max-width: 991px) 40vw,
 *    (max-width: 1199px) 30vw,
 *    (max-width: 4499px) 20vw, --> 4499px is based on 900 / 0,2
 *    900px --> max image width
 * `
 *
 * You might ask: why is this complex 4499px added for the last media query. This is done
 * to make sure the image is only served at its maximum size.
 */
export default (dimensions: Dimensions, maxImgWidth: number): string => {
  const dimensionBreakpoints = Object.keys(dimensions);

  const mediaQueries = dimensionBreakpoints
    .reduce((sizes, dimension, dimensionIndex) => {
      // Because we use max-width, we need the NEXT dimension as delimiter for this media-query.
      const nextDimensionBreakpoint = dimensionBreakpoints[dimensionIndex + 1];
      const vw = dimensions[dimension] * 100;

      // Calculate maximum width of breakpoint where imageWidth < maxImgWidth
      const maxBreakpointSize = maxImgWidth / dimensions[dimension];

      const minWidth = breakpointsPx[dimension] || 0; // current breakpoint
      let maxWidth = breakpointsPx[nextDimensionBreakpoint]; // next breakpoint

      if (!maxWidth) {
        // We're at the largest dimension. Lets limit this largest dimension by
        // the max breakpoint size where imageWidth < maxImgWidth
        maxWidth = maxBreakpointSize;
      }

      if (minWidth > maxWidth || maxWidth > maxBreakpointSize) {
        // If maxWidth > maxBreakpointSize, the image can become bigger
        // than maxImgWidth. Therefore, ignore this breakpoint.
        return sizes;
      }

      return `${sizes}(max-width: ${maxWidth - 1}px) ${vw}vw, `;
    }, '');

  return mediaQueries ? `${mediaQueries}${maxImgWidth}px` : `${maxImgWidth}px`;
};
